/**
 * Player Settings Persistence Hook
 *
 * Manages player settings with localStorage persistence.
 * Settings are automatically saved when changed and restored on mount.
 */

import type {
  BackgroundOpacity,
  FontFamily,
  FontSize,
  SubtitlePosition,
  TextColor,
  TextEdgeStyle,
} from "@lumi/player-core";
import { useEffect, useState } from "react";

const STORAGE_KEY = "lumi:player:settings";

export interface PlayerSettings {
  quality: "auto" | number; // "auto" or level index
  playbackSpeed: number; // 0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2
  subtitlesEnabled: boolean;
  selectedSubtitleId: number | "off";
  subtitlePosition: SubtitlePosition; // 'auto', 'top', 'middle', 'bottom'
  subtitleFontSize: FontSize; // 'small', 'medium', 'large', 'x-large'
  subtitleBackgroundOpacity: BackgroundOpacity; // 0, 25, 50, 75, 100
  subtitleTextColor: TextColor; // 'white', 'yellow', 'cyan', etc.
  subtitleFontFamily: FontFamily; // 'proportional-sans', 'monospace-sans', etc.
  subtitleTextEdge: TextEdgeStyle; // 'none', 'drop-shadow', 'raised', etc.
  volume: number; // 0 to 1
  muted: boolean;
}

const DEFAULT_SETTINGS: PlayerSettings = {
  quality: "auto",
  playbackSpeed: 1,
  subtitlesEnabled: false,
  selectedSubtitleId: "off",
  subtitlePosition: "auto",
  subtitleFontSize: "medium",
  subtitleBackgroundOpacity: 50,
  subtitleTextColor: "white",
  subtitleFontFamily: "proportional-sans",
  subtitleTextEdge: "drop-shadow",
  volume: 1,
  muted: false,
};

/**
 * Hook to manage player settings with localStorage persistence
 *
 * @returns Settings object, update function, and reset function
 */
export function usePlayerSettings() {
  const [settings, setSettings] = useState<PlayerSettings>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as Partial<PlayerSettings>;
        // Merge with defaults to handle new settings in updates
        return { ...DEFAULT_SETTINGS, ...parsed };
      }
    } catch (error) {
      console.warn("Failed to load player settings from localStorage:", error);
    }
    return DEFAULT_SETTINGS;
  });

  // Save to localStorage whenever settings change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.warn("Failed to save player settings to localStorage:", error);
    }
  }, [settings]);

  /**
   * Update one or more settings
   */
  const updateSettings = (partial: Partial<PlayerSettings>) => {
    setSettings((prev) => ({ ...prev, ...partial }));
  };

  /**
   * Reset all settings to defaults and clear localStorage
   */
  const resetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn("Failed to clear player settings from localStorage:", error);
    }
  };

  return { settings, updateSettings, resetSettings };
}
